import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type SidebarState = 'expanded' | 'collapsed' | 'hidden';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: MenuItem[];
  isExpanded?: boolean;
}

interface SidebarStore {
  // Sidebar state
  sidebarState: SidebarState;
  setSidebarState: (state: SidebarState) => void;
  
  // Menu items
  menuItems: MenuItem[];
  
  // Active menu
  activeMenu: string;
  setActiveMenu: (menuId: string) => void;
  
  // Actions
  expandSidebar: () => void;
  collapseSidebar: () => void;
  hideSidebar: () => void;
  toggleSidebar: () => void;
}



const defaultMenuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: '대시보드',
    icon: 'BarChart3',
    path: '/dashboard',
    children: [
      { id: 'todo', label: '해야 할 일', icon: 'CheckSquare', path: '/dashboard/todo' },
      { id: 'urgent', label: '긴급', icon: 'AlertTriangle', path: '/dashboard/urgent' },
      { id: 'favorites', label: '즐겨찾기', icon: 'Star', path: '/dashboard/favorites' },
    ]
  },
  {
    id: 'quote',
    label: '견적',
    icon: 'FileText',
    path: '/quote',
    children: [
      { id: 'quote-create', label: '견적 생성', icon: 'Plus', path: '/quote/create' },
      { id: 'quote-history', label: '견적 내역', icon: 'History', path: '/quote/history' },
    ]
  },
  {
    id: 'order',
    label: '주문',
    icon: 'ShoppingCart',
    path: '/order',
    children: [
      { id: 'order-jamake', label: 'Jamake', icon: 'Package', path: '/order/jamake' },
    ]
  },
  {
    id: 'work',
    label: '작업',
    icon: 'FolderOpen',
    path: '/work',
    children: [
      { id: 'project-create', label: '프로젝트 생성', icon: 'Plus', path: '/work/project-create' },
      { id: 'project', label: '프로젝트', icon: 'Package', path: '/work/project' },
      { id: 'work-manage', label: '작업', icon: 'Settings', path: '/work/manage' },
      { id: 'work-favorites', label: '즐겨찾기', icon: 'Star', path: '/work/favorites' },
      { id: 'task-types', label: '작업유형', icon: 'Layers', path: '/work/task-types' },
      { id: 'work-presets', label: '작업프리셋', icon: 'BookOpen', path: '/work/presets' },
      { id: 'workers', label: '작업자', icon: 'User', path: '/work/workers' },
      { id: 'worker-groups', label: '작업자 그룹', icon: 'Users', path: '/work/worker-groups' },
    ]
  },
  {
    id: 'clients',
    label: '고객사',
    icon: 'Building',
    path: '/clients',
    children: [
      { id: 'client-manage', label: '고객사 관리', icon: 'Users', path: '/clients/manage' },
    ]
  },
  {
    id: 'automation',
    label: '자동화',
    icon: 'Zap',
    path: '/automation',
    children: [
      { id: 'automation-rules', label: '자동화 규칙', icon: 'Settings', path: '/automation/rules' },
      { id: 'workflow-templates', label: '워크플로 템플릿', icon: 'GitBranch', path: '/automation/templates' },
    ]
  }
];

export const useSidebarStore = create<SidebarStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        sidebarState: 'expanded',
        menuItems: defaultMenuItems,
        activeMenu: 'order-jamake',
        
        // Setters
        setSidebarState: (state) => set({ sidebarState: state }),
        setActiveMenu: (menuId) => set({ activeMenu: menuId }),
        
        // Actions
        expandSidebar: () => set({ sidebarState: 'expanded' }),
        collapseSidebar: () => set({ sidebarState: 'collapsed' }),
        hideSidebar: () => set({ sidebarState: 'hidden' }),
        
        toggleSidebar: () => set((state) => ({
          sidebarState: state.sidebarState === 'expanded' ? 'collapsed' : 'expanded'
        })),
      }),
      {
        name: 'sidebar-storage',
        partialize: (state) => ({
          sidebarState: state.sidebarState,
          activeMenu: state.activeMenu,
        }),
      }
    ),
    {
      name: 'sidebar-store',
    }
  )
);

export type { MenuItem };